import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import ServiceManager from './ServiceManager';
import IndustryManager from './IndustryManager';
import ClientManager from './ClientManager';
import OrderManager from './OrderManager';
import SubscriptionManager from './SubscriptionManager';
import EmailManager from './EmailManager';
import PaymentLinksManager from './PaymentLinksManager';
import SettingsManager from './SettingsManager';
import { adminService } from '../../services/adminService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    | 'services'
    | 'clients'
    | 'industries'
    | 'orders'
    | 'subscriptions'
    | 'payments'
    | 'emails'
    | 'settings'
  >('services');
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const token = Cookies.get('adminToken');
    if (!token || !adminService.validateToken(token)) {
      navigate('/admin/login');
      return;
    }
    
    const userData = adminService.getUserByToken(token);
    setUser(userData);
    setIsLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    Cookies.remove('adminToken');
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-orange"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Couteau Suisse Digital</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600">Connecté en tant que {user?.email}</p>
              </div>
              <div className="flex items-center gap-4">
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Accueil</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'services'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Services
                </button>
                <button
                  onClick={() => setActiveTab('industries')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'industries'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Métiers
                </button>
                <button
                  onClick={() => setActiveTab('clients')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'clients'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Clients
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'orders'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Commandes
                </button>
                <button
                  onClick={() => setActiveTab('subscriptions')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'subscriptions'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Abonnements
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'payments'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Paiements
                </button>
                <button
                  onClick={() => setActiveTab('emails')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'emails'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Emails
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Settings
                </button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {activeTab === 'services' && <ServiceManager />}
              {activeTab === 'industries' && <IndustryManager />}
              {activeTab === 'clients' && <ClientManager />}
              {activeTab === 'orders' && <OrderManager />}
              {activeTab === 'subscriptions' && <SubscriptionManager />}
              {activeTab === 'payments' && <PaymentLinksManager />}
              {activeTab === 'emails' && (
                <EmailManager onOpenSettings={() => setActiveTab('settings')} />
              )}
              {activeTab === 'settings' && <SettingsManager />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
